import * as React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getTranslate } from 'react-localize-redux';
import { Link } from 'react-router-dom';
import { bindActionCreators } from 'redux';
import classnames from 'classnames';
import {
    withStyles,
    Typography,
    Stepper,
    Step,
    StepLabel,
    Button,
    LinearProgress,
} from '@material-ui/core';

import ContactPersonList from './components/ContactPersonList';
import StudentTeacherList from './components/StudentTeacherList';
import EditTeachers from './components/EditTeachers';
import SkillInfo from './components/SkillInfo';
import WeekOverview from './components/WeekOverview';
import EditDetails from './components/EditDetails';

import {
    InfoBox, TopActionBar, NoContentClickToAdd, VimoLink, AddForm,
} from '../../common/components';
import { SkillIcon } from '../../common/icons';
import * as PatientStore from '../../app/store/PatientStore';
import { getNumberOfWeeks, getWeekNumber } from '../../youwell-common/dateUtils';
import { DialogButton } from '../../youwell-common/components';

import urls from '../../app/urls';
import { green3 } from '../../app/style/theme';
import ResourceStudentList from './components/ResourceStudentList';
import FormList from './components/FormList';
import EditContactPerson from './components/EditContactPerson';
import { appRoles, orgLicence } from '../../youwell-common/constants';
import EditStudentAccessCode from './components/EditStudentAccessCode';

const styles = theme => ({
    root: {
    },
    content: {
        padding: theme.spacing(3),
        display: 'flex',

        [theme.breakpoints.down('sm')]: {
            flexWrap: 'wrap',
        },
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column',
            padding: theme.spacing(1),
        },
    },
    column: {
        width: `calc(100% / 3 - ${theme.spacing(2)}px)`,
        margin: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',

        [theme.breakpoints.down('sm')]: {
            width: `calc(100% / 2 - ${theme.spacing(2)}px)`,
            flex: '1 1 auto',
            marginTop: 0,
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            margin: 0,
        },
    },
    mergedOnSmall: {
        [theme.breakpoints.only('sm')]: {
            display: 'flex',
            justifyContent: 'space-between',
        },
    },
    infoBox: {
        marginBottom: theme.spacing(2),
        flex: '1 1 auto',
    },
    infoBoxMergedOnSmall: {
        [theme.breakpoints.only('sm')]: {
            width: `calc(100% / 2 - ${theme.spacing(1)}px)`,
            flex: 'none',
        },
    },
    progress: {
        background: 'transparent',
        width: '100%',
        overflow: 'hidden',
    },
    mobileStepper: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
    },
    linearProgress: {
        marginBottom: theme.spacing(1),
    },
    actionButtons: {
        margin: theme.spacing(4),
        borderTop: theme.borders.normal,
        paddingTop: theme.spacing(4),
        display: 'flex',
        justifyContent: 'flex-end',
    },
    actionButton: {
        marginLeft: theme.spacing(2),
    },
    deleteButton: {
        marginLeft: theme.spacing(2),
        color: theme.colors.red1,
        borderColor: theme.colors.red1,
        '&:hover': {
            backgroundColor: theme.colors.red2,
        },
    },
    weekplan: {
        margin: theme.spacing(2, 4),

        [theme.breakpoints.down('xs')]: {
            margin: 0,
            padding: theme.spacing(1),
        },
    },
    accessCode: {
        marginLeft: theme.spacing(1),
        fontSize: 'larger',
        textTransform: 'uppercase',
    },
});

class Home extends React.Component {
    deleteStudent = () => {
        this.props.deleteElement(this.props.studentData, {
            redirect: urls.inside.home,
        });
    }

    render() {
        const {
            classes, translate, profileData, studentData, appConfig, clientConfig,
        } = this.props;

        if (!studentData) {
            return null;
        }

        const isTeacher = appRoles.isTherapist([profileData?.selectedRole?.role]);
        const hasOrgLicense = profileData?.selectedRole.license >= orgLicence.Trial;

        const { currentSkills } = studentData;
        const resourceStudentList = studentData?.extendedProperties?.resourceStudents && JSON.parse(studentData.extendedProperties.resourceStudents);

        const weeksList = currentSkills?.map(skill => skill && getNumberOfWeeks(skill.startTime, skill.endTime));
        const currentWeeks = currentSkills?.map(skill => skill && ((getWeekNumber() - getWeekNumber(skill.startTime) + 52) % 52) + 1);

        return (
            <div className={classes.root}>
                <TopActionBar
                    studentData={studentData}
                    backLink={{
                        to: `${urls.inside.home}`,
                        text: translate('student.backToOverview'),
                    }}
                />

                <div className={classes.weekplan}>
                    {currentSkills?.map((skill, index) => (
                        <WeekOverview
                            key={skill.id}
                            skill={skill}
                            showDayHeadings={index === 0}
                            studentData={studentData}
                        />
                    ))}
                </div>

                <div className={classes.content}>
                    <div className={classes.column}>
                        {currentSkills?.length
                            ? currentSkills.map((skill, index) => (
                                <InfoBox
                                    key={skill.id}
                                    className={classes.infoBox}
                                    heading={translate('student.skill')}
                                    Icon={SkillIcon}
                                    primary={skill.module.name}
                                    link={{ to: `${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.plan}`, text: translate('student.goToPlan') }}
                                    InfoContent={SkillInfo}
                                    InfoContentProps={{ skillId: skill.module.id }}
                                    InfoContentHeader={skill.module.name}
                                >
                                    {weeksList[index] > 5 ? (
                                        <div className={classes.mobileStepper}>
                                            <LinearProgress variant="determinate" value={currentWeeks[index] / weeksList[index] * 100} className={classes.linearProgress} />
                                            <Typography variant="caption" color="primary">
                                                {translate('student.weekXofX', { current: currentWeeks[index], total: weeksList[index] })}
                                            </Typography>
                                        </div>
                                    ) : (
                                        <Stepper activeStep={currentWeeks[index] - 1} alternativeLabel className={classes.progress}>
                                            {[...Array(weeksList[index]).keys()].map(i => (
                                                <Step key={i}>
                                                    <StepLabel>{`${i + 1}/${weeksList[index]}`}</StepLabel>
                                                </Step>
                                            ))}
                                        </Stepper>
                                    )}
                                </InfoBox>
                            ))

                            : (
                                <InfoBox
                                    className={classes.infoBox}
                                    heading={translate('student.skill')}
                                >
                                    <NoContentClickToAdd
                                        text={translate('dashboard.noCurrentSkill')}
                                        active
                                        linkText={translate('student.clickToStartPlan')}
                                        linkTo={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.plan}`}
                                        readOnly={!isTeacher}
                                    />
                                </InfoBox>
                            )}
                        {/* {todaysTasks && todaysTasks.map(task => (
                            <InfoBox
                                key={task.id}
                                className={classes.infoBox}
                                heading={translate('student.todaysTask')}
                                simpleIcon
                                primary={task.name}
                                link={{ to: `${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.task}/${task.id}`, text: translate('dashboard.goToTask') }}
                                color={getTaskColor(task)}
                            />
                        ))} */}
                        <div className={classes.mergedOnSmall}>
                            {!clientConfig.u_AnonymousOnly && (
                                <InfoBox
                                    className={classnames(classes.infoBox, classes.infoBoxMergedOnSmall)}
                                    heading={translate('student.contactPersons')}
                                    AddContent={isTeacher ? EditContactPerson : null}
                                    AddContentProps={{ patientId: studentData.id }}
                                    ModalProps={{ wide: true }}
                                >
                                    {studentData.contactPersons?.length > 0 ? (
                                        <ContactPersonList persons={studentData.contactPersons} patientId={studentData.id} readOnly={!isTeacher} />
                                    ) : (
                                        <Typography variant="caption">
                                            {translate('student.noContacts')}
                                        </Typography>
                                    )}
                                </InfoBox>
                            )}

                            <InfoBox
                                className={classnames(classes.infoBox, classes.infoBoxMergedOnSmall)}
                                heading={translate('student.resourceStudents')}
                            >
                                {resourceStudentList && resourceStudentList.length > 0 ? (
                                    <ResourceStudentList persons={resourceStudentList} />
                                ) : (
                                    <Typography variant="caption">
                                        {translate('student.noResourceStudents')}
                                    </Typography>
                                )}
                            </InfoBox>
                        </div>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.teachers')}
                            EditContent={isTeacher && hasOrgLicense ? EditTeachers : null}
                        >
                            <StudentTeacherList studentData={studentData} />
                        </InfoBox>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.studentAccess')}
                            EditContent={isTeacher && studentData.programData ? EditStudentAccessCode : null}
                            EditContentProps={{ programData: studentData.programData, patientId: studentData.id }}
                        >
                            {studentData.programData?.accessCode ? (
                                <Typography>
                                    {`${translate('student.accessCode')}:`}
                                    <span className={classes.accessCode}>{studentData.programData.accessCode}</span>
                                </Typography>
                            ) : (
                                <Typography color="textSecondary">
                                    {translate('student.noAccessCodeRegistered')}
                                </Typography>
                            )}
                        </InfoBox>
                    </div>
                    <div className={classes.column}>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.behaviour')}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'extendedProperties.behaviour' }}
                            color={green3}
                        >
                            <Typography>
                                {studentData.extendedProperties?.behaviour}
                            </Typography>
                        </InfoBox>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.hypothesis')}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'extendedProperties.hypothesis' }}
                            color={green3}
                        >
                            <Typography>
                                {studentData.extendedProperties?.hypothesis}
                            </Typography>
                        </InfoBox>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.socialSkill')}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'extendedProperties.socialSkill' }}
                            color={green3}
                        >
                            <Typography>
                                {studentData.extendedProperties?.socialSkill}
                            </Typography>
                        </InfoBox>
                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.environmentAdjustments')}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'extendedProperties.environmentAdjustments' }}
                            color={green3}
                        >
                            <Typography>
                                {studentData.extendedProperties?.environmentAdjustments}
                            </Typography>
                        </InfoBox>
                    </div>
                    <div className={classes.column}>
                        {appConfig.mappingFormTaskId && (
                            <InfoBox
                                className={classes.infoBox}
                                heading={translate('student.surveyForms')}
                                AddContent={AddForm}
                                AddContentHeader={translate('student.addSurveyForm')}
                                AddContentProps={{
                                    studentId: studentData.id, patientProgramId: studentData.programData?.id, formId: appConfig.mappingFormTaskId,
                                }}
                            >
                                <FormList studentId={studentData.id} patientProgramId={studentData.programData?.id} />
                            </InfoBox>
                        )}
                        <InfoBox
                            className={classes.infoBox}
                            heading={`${translate('student.about')} ${studentData.name}`}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'description' }}
                        >
                            <Typography>
                                {studentData.description}
                            </Typography>
                        </InfoBox>

                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.progression')}
                        >
                            <VimoLink to={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.progress}`} text={translate('student.showProgression')} forward className={classes.link} />
                        </InfoBox>

                        <InfoBox
                            className={classes.infoBox}
                            heading={translate('student.motivation')}
                            EditContent={isTeacher ? EditDetails : null}
                            EditContentProps={{ detailName: 'extendedProperties.motivation' }}
                        >
                            <Typography>
                                {studentData.extendedProperties?.motivation}
                            </Typography>
                        </InfoBox>
                    </div>
                </div>

                {isTeacher && (
                    <div className={classes.actionButtons}>
                        <DialogButton
                            buttonText={translate('student.deleteStudent')}
                            dialogTitle={translate('student.confirmDeleteStudentTitle')}
                            dialogCancelText={translate('common.cancel')}
                            dialogOkAction={this.deleteStudent}
                            buttonProps={{
                                className: classes.deleteButton,
                            }}
                        />
                        <Button
                            to={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.plan}`}
                            component={Link}
                            variant="outlined"
                            color="primary"
                            className={classes.actionButton}
                        >
                            {translate('student.editPlan')}
                        </Button>
                        <Button
                            to={`${urls.inside.student.home}/${studentData.id}/${urls.inside.student.parts.edit}`}
                            component={Link}
                            variant="outlined"
                            color="primary"
                            className={classes.actionButton}
                        >
                            {translate('student.editStudent')}
                        </Button>
                    </div>
                )}
            </div>
        );
    }
}

Home.propTypes = {
    classes: PropTypes.object.isRequired,
    translate: PropTypes.func.isRequired,
    clientConfig: PropTypes.object.isRequired,
    match: PropTypes.shape({
        params: PropTypes.shape({
            studentId: PropTypes.string.isRequired,
        }).isRequired,
    }).isRequired,
    profileData: PropTypes.object,
    studentData: PropTypes.object,
    deleteElement: PropTypes.func.isRequired,
    appConfig: PropTypes.object.isRequired,
};

Home.defaultProps = {
    profileData: null,
    studentData: null,
};

const mapStateToProps = (state, ownProps) => ({
    translate: getTranslate(state.localize),
    appConfig: state.pageSettings.appConfig,
    clientConfig: state.auth.clientConfig,
    profileData: state.auth.profileData,
    studentData: state.patient.details && state.patient.details[ownProps.match.params.studentId],
});

const mapDispatchToProps = dispatch => ({
    ...bindActionCreators(PatientStore.actionCreators, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(Home));
